package com.vizio.redwolf.pay.client

import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.SUBSCRIPTION_API_KEY
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.safeHttpCall
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.parameter
import io.ktor.client.statement.HttpResponse
import io.ktor.http.URLProtocol
import io.ktor.http.path

/**
 * API for online products offered on Vizio.com
 * Reference: https://app-vizio-ecommerce-products-dev.azurewebsites.net/swagger/index.html
 */
class ProductApiHttpClient(private val payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) :
    PayApiHttpClient(clientConfig) {

    private val payPath = payEnv.productsPath
    private val apiSubscriptionKey = payEnv.vizioApiSubscriptionKey

    suspend fun getProducts(productIds: List<String> = emptyList()): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/products")
                    if (productIds.isNotEmpty()) {
                        parameter("ids", productIds.joinToString(separator = ","))
                    }
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getCategories(): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/categories")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getProductsByCategory(categoryId: String): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/products/categories/$categoryId")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getCategory(categoryId: String): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/categories/$categoryId")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getProductTypes(): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/product-types")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getProductById(id: String): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/products/$id")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getVersion(): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/version")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, apiSubscriptionKey)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    private companion object {
        private const val SUBSCRIPTION_KEY = "Ocp-Apim-Subscription-Key"
        private const val SCHEME_HTTPS = "https"
    }

}
